// src/components/common/Navbar.ts - Version refactorisée
import { colors, navLinks, socialLinks, cssClasses, sections } from './NavbarConfig';
import '../../styles/components/navbar.css';
import logoUrl from '../../assets/logo-final.png';

export default class Navbar {
  private element: HTMLElement;
  private scrolled: boolean = false;
  private mobileMenuOpen: boolean = false;
  private lastScrollTop: number = 0;
  private navbarHidden: boolean = false;
  private touchStartY: number = 0;
  private static instance: Navbar | null = null;

  constructor() {
    this.element = document.createElement('header');
    this.element.className = 'fixed w-full top-0 left-0 z-50 transition-all duration-500';
    this.render();
    this.setupEventListeners();

    // Marquer le body pour permettre les correctifs d'espacement globaux
    document.body.classList.add('has-navbar');

    // Singleton pattern pour s'assurer qu'une seule instance existe
    if (Navbar.instance) {
      return Navbar.instance;
    }
    Navbar.instance = this;
  }

  // Méthode statique pour initialiser la navbar sur toutes les pages
  public static initialize(): void {
    // S'assurer que la méthode est appelée après le chargement du DOM
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', () => {
        Navbar.createAndMount();
      });
    } else {
      Navbar.createAndMount();
    }
  }

  // Méthode pour créer et monter la navbar dans le DOM
  private static createAndMount(): void {
    const navbar = new Navbar();

    // Chercher d'abord un conteneur spécifique avec l'ID navbar-container
    const navbarContainer = document.getElementById('navbar-container');

    if (navbarContainer) {
      // Si un conteneur spécifique existe, insérer la navbar dedans
      navbarContainer.appendChild(navbar.getElement());
    } else {
      // Si aucun conteneur spécifique n'existe, chercher un header générique
      const headerContainer = document.querySelector('header:not(#navbar-container)');

      if (headerContainer) {
        // Si un header existe, le remplacer
        headerContainer.replaceWith(navbar.getElement());
      } else {
        // Sinon, ajouter au début du body
        document.body.prepend(navbar.getElement());
      }
    }

    // Ajouter une classe au body pour l'espacement correct
    document.body.classList.add('has-navbar');
  }

  /* --- RENDU DES COMPOSANTS --- */

  private render(): void {
    this.element.innerHTML = `
      ${this.renderMainNavbar()}
      ${this.renderMobileMenu()}
    `;
  }

  private renderMainNavbar(): string {
    return `
      <!-- Navbar principale -->
      <div id="navbar-surface" class="bg-gradient-to-b from-[#0b1220] via-[#0b1220]/85 to-[#0b1220]/70 backdrop-blur-xl text-white shadow-lg transition-all duration-500">
        <div class="container">
          <div class="flex justify-between items-center h-20">
            ${this.renderLogo()}
            ${this.renderDesktopNavigation()}
            ${this.renderMobileMenuButton()}
          </div>
        </div>
      </div>
    `;
  }

  private renderLogo(): string {
    return `
      <!-- Logo avec animation subtile -->
      <div class="flex items-center">
        <a href="/" class="relative">
          <div class="relative z-10 flex items-center">
            <img src="${logoUrl}" alt="World Of Numeric Logo" class="h-10">
          </div>
        </a>
      </div>
    `;
  }

  private renderDesktopNavigation(): string {
    const activeLinks = navLinks.map(link => {
      const isActive = this.isActivePage(link.href, this.getCurrentPath());
      return {
        ...link,
        active: isActive
      };
    });

    return `
      <!-- Navigation Links - Desktop -->
      <nav class="hidden lg:flex items-center">
        <div class="relative bg-white/5 backdrop-blur-sm rounded-full px-1 py-1 mr-3 overflow-hidden">
          <!-- Effet lumineux qui se déplace subtil -->
          <div class="absolute inset-0 bg-gradient-to-r from-[#ed4c67]/5 via-[#3498db]/10 to-[#ed4c67]/5 w-[200%] animate-pulse-slow opacity-0 hover:opacity-100 transition-opacity duration-700"></div>
          <div class="flex items-center space-x-1 relative">
            <!-- Indicateur actif qui se déplace -->
            <span id="nav-indicator" class="absolute h-full bg-gradient-to-r from-[#ed4c67]/20 to-[#3498db]/20 rounded-full transition-all duration-300 pointer-events-none"></span>

            ${activeLinks.map(link => `
              <a href="${link.href}" class="nav-link relative px-5 py-2 text-white/${link.active ? '90' : '75'} font-medium transition-colors duration-300 rounded-full hover:text-white hover:bg-white/5" data-index="${link.index}">
                <span class="relative z-10">${link.text}</span>
              </a>
            `).join('')}
          </div>
        </div>

        ${this.renderContactButton()}
      </nav>
    `;
  }

  private renderContactButton(): string {
    return `
      <!-- Bouton Contact - Fill depuis la gauche (Bleu) -->
      <a href="/contact.html" class="group relative inline-flex items-center overflow-hidden px-5 py-2 rounded-full bg-transparent border-2 border-won-blue text-won-blue font-semibold transition-all duration-500 hover:text-white hover:shadow-xl hover:shadow-won-blue/25 hover:scale-105">
        <span class="absolute inset-0 bg-[#3498db] scale-x-0 group-hover:scale-x-100 transition-transform duration-500 origin-left rounded-full"></span>

        <span class="relative flex items-center justify-center z-10">
          <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4 mr-2 group-hover:rotate-12 transition-transform duration-300" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 8l7.89 4.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
          </svg>
          Contact
          <svg xmlns="http://www.w3.org/2000/svg" class="h-3 w-3 ml-2 group-hover:translate-x-1 transition-transform duration-300" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="3" d="M9 5l7 7-7 7" />
          </svg>
        </span>
      </a>
    `;
  }

  private renderMobileMenuButton(): string {
    return `
      <!-- Mobile Menu Button avec animation élégante -->
      <div class="lg:hidden flex items-center">
        <button id="mobile-menu-button" class="text-white focus:outline-none flex items-center space-x-2 bg-white/5 hover:bg-white/10 px-3 py-2 rounded-full transition-colors duration-300" aria-label="Toggle menu" aria-expanded="false">
          <span class="text-sm font-medium hidden sm:inline-block mr-2">Menu</span>
          <div class="relative w-6 h-5 flex flex-col justify-center">
            <span id="bar1" class="absolute w-6 h-0.5 bg-white rounded-full transform transition-all duration-300 -translate-y-2"></span>
            <span id="bar2" class="absolute w-4 h-0.5 bg-white rounded-full right-0 transform transition-all duration-300"></span>
            <span id="bar3" class="absolute w-6 h-0.5 bg-white rounded-full transform transition-all duration-300 translate-y-2"></span>
          </div>
        </button>
      </div>
    `;
  }

  private renderMobileMenu(): string {
    return `
      <!-- Menu Mobile -->
      <div id="mobile-menu" class="fixed top-0 left-0 w-full h-0 overflow-hidden z-40 bg-[#34495e]/95 backdrop-blur-xl transition-all duration-500 lg:hidden">
        <!-- Contenu scrollable du menu -->
        <div id="mobile-content" class="w-full h-full overflow-y-auto pt-24 pb-8">
          <div class="container mx-auto px-4">
            <div class="flex justify-end mb-6">
              <button id="mobile-menu-close" class="flex items-center space-x-2 rounded-full bg-white/10 hover:bg-white/20 text-white/80 hover:text-white px-4 py-2 transition" aria-label="Fermer le menu">
                <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
                </svg>
                <span>Fermer</span>
              </button>
            </div>
            ${this.renderMobileNavigation()}
            ${this.renderMobileContactInfo()}
          </div>
        </div>
      </div>
    `;
  }

  private renderMobileNavigation(): string {
    const currentPath = this.getCurrentPath();

    return `
      <nav class="flex flex-col items-center space-y-4 max-w-sm mx-auto">
        ${navLinks.map((link, index) => `
          <a href="${link.href}"
             class="mobile-link w-full text-center py-3 px-4 text-white text-xl font-medium rounded-xl hover:bg-white/5 transition-all duration-300 ${this.isActivePage(link.href, currentPath) ? 'active' : ''}"
             aria-current="${this.isActivePage(link.href, currentPath) ? 'page' : 'false'}"
             style="opacity: 0; animation: fadeInDown 0.3s forwards ${index * 0.1}s;">
            ${link.text}
          </a>
        `).join('')}

        <!-- Ligne de séparation -->
        <div class="w-full h-px bg-gradient-to-r from-transparent via-white/10 to-transparent my-2"></div>

        <a href="/contact.html"
           class="mt-4 w-full py-4 rounded-xl bg-gradient-to-r from-[#ed4c67] to-[#3498db] text-white font-medium text-center relative overflow-hidden group"
           style="opacity: 0; animation: fadeInDown 0.3s forwards 0.6s;">
          <span class="absolute inset-0 bg-gradient-to-r from-[#3498db] to-[#ed4c67] opacity-0 group-hover:opacity-100 transition-opacity duration-500"></span>
          <span class="relative z-10 flex justify-center items-center">
            Contactez-nous
            <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5 ml-2 group-hover:translate-x-1 transition-transform duration-300" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M14 5l7 7m0 0l-7 7m7-7H3" />
            </svg>
          </span>
        </a>
      </nav>
    `;
  }

  private renderMobileContactInfo(): string {
    return `
      <!-- Informations de contact et réseaux sociaux -->
      <div class="mt-12 border-t border-white/10 pt-6 text-center max-w-sm mx-auto" style="opacity: 0; animation: fadeInUp 0.5s forwards 0.7s;">
        <p class="text-gray-400 mb-2">contact@worldofnumeric.fr</p>

        <!-- Réseaux sociaux -->
        <div class="flex justify-center space-x-4 mt-6">
          ${socialLinks.map((link, index) => `
            <a href="${link.href}"
               target="_blank"
               rel="noopener noreferrer"
               class="social-link w-12 h-12 rounded-full flex items-center justify-center bg-white/5 text-gray-400 hover:bg-gradient-to-br hover:from-[#ed4c67] hover:to-[#3498db] hover:text-white transition-all duration-300"
               style="opacity: 0; animation: fadeIn 0.3s forwards ${0.8 + (index * 0.1)}s;">
              <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5" fill="currentColor" viewBox="0 0 24 24">
                <path d="${link.icon}"/>
              </svg>
            </a>
          `).join('')}
        </div>
      </div>
    `;
  }

  /* --- UTILITAIRES --- */

  private getCurrentPath(): string {
    return window.location.pathname + window.location.hash;
  }

  private isActivePage(linkHref: string, currentPath: string): boolean {
    // Extraction du nom de fichier et du hash
    const [linkFile, linkHash] = linkHref.split('#');
    const currentFile = currentPath.split('/').pop()?.split('#')[0] || '';
    const currentHash = window.location.hash.replace('#', '');

    // Vérification de la page d'accueil
    if (linkFile === 'index.html' && !linkHash &&
        (currentFile === '' || currentFile === 'index.html') && !currentHash) {
      return true;
    }

    // Vérification des autres pages
    if (linkFile && currentFile && linkFile === currentFile) {
      // Si le lien a un hash, vérifier qu'il correspond
      if (linkHash) {
        return linkHash === currentHash;
      }
      // Sinon, c'est juste la bonne page
      return true;
    }

    return false;
  }

  /* --- GESTIONNAIRES D'ÉVÉNEMENTS --- */

  private setupEventListeners(): void {
    // Éléments DOM nécessaires aux événements
    const elements = this.getNavbarElements();

    // Configuration des événements
    this.setupMobileMenuEvents(elements);
    this.setupNavigationIndicator(elements);
    this.setupScrollEvents(elements);
    this.setupResizeHandler(elements);
    this.setupTouchEvents(elements);
    this.setupAccessibility(elements);
  }

  private getNavbarElements(): any {
    return {
      mobileMenuButton: this.element.querySelector('#mobile-menu-button'),
      mobileMenu: this.element.querySelector('#mobile-menu'),
      mobileContent: this.element.querySelector('#mobile-content'),
      bar1: this.element.querySelector('#bar1'),
      bar2: this.element.querySelector('#bar2'),
      bar3: this.element.querySelector('#bar3'),
      navbarContainer: this.element.querySelector('#navbar-surface'),
      navIndicator: this.element.querySelector('#nav-indicator'),
      navLinks: this.element.querySelectorAll('.nav-link'),
      navContainer: this.element.querySelector('nav'),
      mobileLinks: this.element.querySelectorAll('.mobile-link'),
      socialLinks: this.element.querySelectorAll('.social-link')
    };
  }

  private setupMobileMenuEvents(elements: any): void {
    const { mobileMenuButton, mobileMenu, bar1, bar2, bar3, mobileContent } = elements;

    const mobileCloseButton = this.element.querySelector('#mobile-menu-close');

    if (mobileMenuButton && mobileMenu && bar1 && bar2 && bar3 && mobileContent) {
      mobileMenuButton.addEventListener('click', () => {
        this.toggleMobileMenu(mobileMenu, mobileContent, bar1, bar2, bar3, mobileMenuButton);
      });

      if (mobileCloseButton) {
        mobileCloseButton.addEventListener('click', () => {
          if (this.mobileMenuOpen) {
            this.toggleMobileMenu(mobileMenu, mobileContent, bar1, bar2, bar3, mobileMenuButton);
          }
        });
      }

      // Fermer le menu lors d'un clic à l'intérieur du menu (sur les liens)
      const mobileLinks = mobileMenu.querySelectorAll('a');
      mobileLinks.forEach((link: Element) => {
        link.addEventListener('click', () => {
          if (this.mobileMenuOpen) {
            this.toggleMobileMenu(mobileMenu, mobileContent, bar1, bar2, bar3, mobileMenuButton);
          }
        });
      });
    }

    // Fermer le menu avec la touche Escape
    document.addEventListener('keydown', (e: KeyboardEvent) => {
      if (e.key === 'Escape' && this.mobileMenuOpen && mobileMenu && mobileContent && bar1 && bar2 && bar3 && mobileMenuButton) {
        this.toggleMobileMenu(mobileMenu, mobileContent, bar1, bar2, bar3, mobileMenuButton);
      }
    });
  }

  private toggleMobileMenu(
    mobileMenu: Element,
    mobileContent: Element,
    bar1: Element,
    bar2: Element,
    bar3: Element,
    mobileMenuButton: Element
  ): void {
    this.mobileMenuOpen = !this.mobileMenuOpen;

    // Mettre à jour l'attribut aria-expanded
    mobileMenuButton.setAttribute('aria-expanded', this.mobileMenuOpen.toString());

    if (this.mobileMenuOpen) {
      // S'assurer que la navbar est visible avant d'ouvrir le menu
      this.element.style.transform = 'translateY(0)';
      this.navbarHidden = false;

      // Ouvrir le menu
      document.body.style.overflow = 'hidden';
      mobileMenu.classList.add('h-screen');

      // Animation du bouton hamburger
      bar1.classList.add('rotate-45', 'translate-y-0');
      bar1.classList.remove('-translate-y-2');

      bar2.classList.add('opacity-0', 'translate-x-full');

      bar3.classList.add('-rotate-45', 'translate-y-0');
      bar3.classList.remove('translate-y-2');
    } else {
      // Fermer le menu
      document.body.style.overflow = '';
      mobileMenu.classList.remove('h-screen');

      // Animation du bouton hamburger
      bar1.classList.remove('rotate-45', 'translate-y-0');
      bar1.classList.add('-translate-y-2');

      bar2.classList.remove('opacity-0', 'translate-x-full');

      bar3.classList.remove('-rotate-45', 'translate-y-0');
      bar3.classList.add('translate-y-2');
    }
  }

  private setupNavigationIndicator(elements: any): void {
    const { navLinks, navContainer, navIndicator } = elements;

    if (!navIndicator) return;

    // Fonction pour mettre à jour la position de l'indicateur
    const updateIndicator = (index: number) => {
      if (!navIndicator || !navLinks[index]) return;

      // Obtenir l'élément actif
      const activeLink = navLinks[index] as HTMLElement;
      const linkRect = activeLink.getBoundingClientRect();
      const navRect = navContainer?.getBoundingClientRect() || { left: 0 };

      // Position relative au conteneur de navigation
      const leftPosition = Math.round(linkRect.left - navRect.left);
      const width = Math.round(linkRect.width);
      const height = Math.round(linkRect.height);

      // Appliquer les styles
      navIndicator.style.transform = `translateX(${leftPosition-3}px)`;
      navIndicator.style.width = `${width}px`;
      navIndicator.style.height = `${height}px`;
      navIndicator.style.top = '0';
      navIndicator.style.left = '0';

      // Vérifier la visibilité
      const isVisible =
        linkRect.top >= 0 &&
        linkRect.bottom <= window.innerHeight &&
        linkRect.width > 0 &&
        linkRect.height > 0;

      navIndicator.style.opacity = isVisible ? '1' : '0';
    };

    // Exposer la fonction pour l'utiliser plus tard
    (window as any).updateNavIndicator = updateIndicator;

    // Initialiser l'indicateur
    setTimeout(() => {
      updateIndicator(0);
    }, 100);

    // Événements hover pour l'indicateur
    navLinks.forEach((link: Element, index: number) => {
      link.addEventListener('mouseenter', () => {
        requestAnimationFrame(() => {
          updateIndicator(index);
        });
      });
    });

    // Remettre l'indicateur à sa position d'origine
    navContainer?.addEventListener('mouseleave', () => {
      const activeIndex = Array.from(navLinks).findIndex((link: unknown) => {
        const element = link as Element;
        const href = element.getAttribute('href') || '';
        return this.isActivePage(href, this.getCurrentPath());
      });
      updateIndicator(activeIndex >= 0 ? activeIndex : 0);
    });

    // Mise à jour de l'indicateur en fonction de la section visible
    window.addEventListener('scroll', () => {
      if (!navLinks.length) return;

      let currentSectionIndex = 0;

      // Déterminer la section actuellement visible
      sections.forEach((section, index) => {
        if (section === '#') return; // Ignorer l'accueil pour cette logique

        const element = document.querySelector(section);
        if (element) {
          const rect = element.getBoundingClientRect();
          if (rect.top <= 200 && rect.bottom >= 200) {
            currentSectionIndex = index;
          }
        }
      });

      // Mettre à jour l'indicateur
      requestAnimationFrame(() => {
        updateIndicator(currentSectionIndex);
      });
    });

    // Initialiser après chargement complet
    window.addEventListener('load', () => {
      setTimeout(() => {
        const currentHash = window.location.hash || '#';
        let activeIndex = 0;

        // Trouver l'élément actif
        navLinks.forEach((link: Element, index: number) => {
          const href = link.getAttribute('href');
          if (href && this.isActivePage(href, this.getCurrentPath())) {
            activeIndex = index;
          }
        });

        updateIndicator(activeIndex);

        // Masquer sur mobile
        if (window.innerWidth < 1024 && navIndicator) {
          navIndicator.style.opacity = '0';
        }
      }, 200);
    });
  }

  private setupScrollEvents(elements: any): void {
    const { navbarContainer } = elements;

    window.addEventListener('scroll', () => {
      const currentScrollTop = window.scrollY;

      // Changement de style lors du défilement
      if (currentScrollTop > 50 && !this.scrolled && navbarContainer) {
        navbarContainer.classList.add('py-2', 'bg-[#0b1220]/95');
        this.scrolled = true;
      } else if (currentScrollTop <= 50 && this.scrolled && navbarContainer) {
        navbarContainer.classList.remove('py-2', 'bg-[#0b1220]/95');
        this.scrolled = false;
      }

      // Masquer/afficher la navbar selon le défilement
      if (currentScrollTop > 300) {
        if (currentScrollTop > this.lastScrollTop && !this.navbarHidden && !this.mobileMenuOpen) {
          this.element.style.transform = 'translateY(-100%)';
          this.navbarHidden = true;
        } else if (currentScrollTop < this.lastScrollTop && this.navbarHidden) {
          this.element.style.transform = 'translateY(0)';
          this.navbarHidden = false;
        }
      } else {
        this.element.style.transform = 'translateY(0)';
        this.navbarHidden = false;
      }

      this.lastScrollTop = currentScrollTop;
    });
  }

  private setupResizeHandler(elements: any): void {
    const { navLinks, navIndicator, mobileMenu } = elements;

    window.addEventListener('resize', () => {
      // Identifier l'élément actif
      const activeIndex = Array.from(navLinks).findIndex((link: unknown) => {
        const element = link as Element;
        const href = element.getAttribute('href') || '';
        return this.isActivePage(href, this.getCurrentPath());
      });

      // Mettre à jour l'indicateur
      requestAnimationFrame(() => {
        if (typeof (window as any).updateNavIndicator === 'function') {
          (window as any).updateNavIndicator(activeIndex >= 0 ? activeIndex : 0);
        }

        // Gérer l'affichage sur mobile
        if (window.innerWidth < 1024) {
          if (navIndicator) navIndicator.style.opacity = '0';
        } else {
          // Fermer le menu mobile en mode desktop
          if (this.mobileMenuOpen && mobileMenu) {
            const { mobileContent, bar1, bar2, bar3, mobileMenuButton } = elements;
            if (mobileContent && bar1 && bar2 && bar3 && mobileMenuButton) {
              this.toggleMobileMenu(mobileMenu, mobileContent, bar1, bar2, bar3, mobileMenuButton);
            }
          }
        }
      });
    });
  }

  private setupTouchEvents(elements: any): void {
    const { mobileMenu } = elements;

    if (!mobileMenu) return;

    // Swipe vers le bas pour fermer le menu
    mobileMenu.addEventListener('touchstart', (e: TouchEvent) => {
      this.touchStartY = e.touches[0].clientY;
    });

    mobileMenu.addEventListener('touchmove', (e: TouchEvent) => {
      if (!this.mobileMenuOpen) return;

      const touchY = e.touches[0].clientY;
      const diff = touchY - this.touchStartY;

      // Si on swipe vers le bas de plus de 100px, fermer le menu
      if (diff > 100) {
        const { mobileContent, bar1, bar2, bar3, mobileMenuButton } = elements;
        if (mobileContent && bar1 && bar2 && bar3 && mobileMenuButton) {
          this.toggleMobileMenu(mobileMenu, mobileContent, bar1, bar2, bar3, mobileMenuButton);
        }
      }
    });
  }

  private setupAccessibility(elements: any): void {
    const { mobileMenuButton, mobileLinks } = elements;

    if (!mobileMenuButton) return;

    // Améliorer l'accessibilité du bouton de menu
    mobileMenuButton.setAttribute('tabindex', '0');

    // Support pour l'activation par clavier
    mobileMenuButton.addEventListener('keydown', (e: KeyboardEvent) => {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        mobileMenuButton.click();
      }
    });

    // Rendre les liens du menu mobile plus accessibles
    if (mobileLinks) {
      mobileLinks.forEach((link: Element) => {
        link.addEventListener('focus', () => {
          link.classList.add('bg-white/5');
        });

        link.addEventListener('blur', () => {
          link.classList.remove('bg-white/5');
        });
      });
    }
  }

  public getElement(): HTMLElement {
    return this.element;
  }
}
